import { AddIcon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Text,
} from "@chakra-ui/react";
import Form from "@rjsf/chakra-ui";
import { FormProps } from "@rjsf/core";
import {
  ArrayFieldTemplateItemType,
  ArrayFieldTemplateProps,
  ArrayFieldTitleProps,
  FieldTemplateProps,
  getSubmitButtonOptions,
  getTemplate,
  getUiOptions,
  ObjectFieldTemplateProps,
  RegistryFieldsType,
  RegistryWidgetsType,
  SubmitButtonProps,
  TitleFieldProps,
  WidgetProps,
} from "@rjsf/utils";
import { IconButtonProps, StrictRJSFSchema } from "@rjsf/utils/src/types";
import { customizeValidator } from "@rjsf/validator-ajv8";
import { Draft, produce } from "immer";
import { JSONSchema7Definition } from "json-schema";
import { noop, startCase } from "lodash-es";
import React, { useMemo } from "react";
import { AccordionIcon } from "shared/components/accordion/AccordionIcon";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { SimpleDateInput } from "shared/components/input/date/SimpleDateInput";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { SimpleSelect } from "shared/components/input/select/SimpleSelect";
import { SimpleTextInput } from "shared/components/input/text/SimpleTextInput";

export interface DynamicFormProps {
  schema: StrictRJSFSchema;
  formData: unknown;
  setFormData: (formData: unknown) => void;
}

const validator = customizeValidator({
  customFormats: {
    muuid: () => true,
    decimal: () => true,
  },
});

export const DynamicForm: React.FC<DynamicFormProps> = ({
  schema,
  formData,
  setFormData,
}) => {
  const fixedSchema = useMemo(() => fixupSchema(schema), [schema]);
  return (
    <Box
      sx={{
        ".chakra-form-control": {
          maxWidth: "450px",
          mb: 0,
        },
        ".form-group": {
          mb: "8px",
        },
      }}
    >
      <Form
        schema={fixedSchema}
        formData={formData}
        validator={validator}
        widgets={widgets}
        fields={fields}
        templates={templates}
        onChange={(e) => setFormData(e.formData)}
        onSubmit={noop}
        // eslint-disable-next-line no-console
        onError={(errors) => console.error(errors)}
      />
    </Box>
  );
};

const CustomSubmitButton: React.FC<SubmitButtonProps> = (props) => {
  const { uiSchema } = props;
  const { norender } = getSubmitButtonOptions(uiSchema);
  if (norender) {
    return null;
  }
  return (
    <FormFooter>
      <FormFooterActions>
        <Button type="submit">Submit</Button>
      </FormFooterActions>
    </FormFooter>
  );
};

const CustomTextWidget: React.FC<WidgetProps> = (props) => (
  <SimpleTextInput
    value={props.value || ""}
    onChangeValue={props.onChange}
    placeholder={startCase(props.label)}
    isDisabled={!!props.schema.const}
  />
);

const CustomSelectWidget: React.FC<WidgetProps> = (props) => (
  <SimpleSelect
    placeholder={startCase(props.label)}
    value={props.value}
    onChangeValue={props.onChange}
  >
    {props.options.enumOptions?.map((option) => (
      <SelectOption value={option.value} key={option.value}>
        {startCase(option.label)}
      </SelectOption>
    ))}
  </SimpleSelect>
);

const CustomDateWidget: React.FC<WidgetProps> = (props) => (
  <SimpleDateInput
    value={props.value}
    onChangeValue={props.onChange}
    placeholder={startCase(props.label)}
    isDisabled={!!props.schema.const}
  />
);

const CustomFieldTemplate: React.FC<FieldTemplateProps> = ({ children }) => (
  <Box>{children}</Box>
);

const CustomTitleFieldTemplate: React.FC<TitleFieldProps> = ({
  title,
  required,
}) => (
  <Text textStyle="h5" mb="8px">
    {startCase(title)} {required ? "*" : null}
  </Text>
);

const CustomObjectFieldTemplate: React.FC<ObjectFieldTemplateProps> = ({
  properties,
  title,
}) => (
  <ExpandingBox title={<Text textStyle="bodyBold">{title}</Text>}>
    <Grid templateColumns="max-content minmax(300px, max-content)" gap={4}>
      {properties.map((element) => (
        <React.Fragment key={element.name}>
          <GridItem pt="12px">{startCase(element.name)}</GridItem>
          <GridItem>{element.content}</GridItem>
        </React.Fragment>
      ))}
    </Grid>
  </ExpandingBox>
);

const widgets: RegistryWidgetsType = {
  TextWidget: CustomTextWidget,
  SelectWidget: CustomSelectWidget,
  DateWidget: CustomDateWidget,
};

const fields: RegistryFieldsType = {};

const CustomArrayFieldTemplate: React.FC<ArrayFieldTemplateProps> = ({
  canAdd,
  disabled,
  idSchema,
  uiSchema,
  items,
  onAddClick,
  readonly,
  registry,
  required,
  schema,
  title,
}) => {
  const uiOptions = getUiOptions(uiSchema);

  const ArrayFieldItemTemplate = getTemplate<"ArrayFieldItemTemplate">(
    "ArrayFieldItemTemplate",
    registry,
    uiOptions,
  );

  const ArrayFieldTitleTemplate = getTemplate<"ArrayFieldTitleTemplate">(
    "ArrayFieldTitleTemplate",
    registry,
    uiOptions,
  );

  const {
    ButtonTemplates: { AddButton },
  } = registry.templates;

  return (
    <ExpandingBox
      title={
        <HStack w="100%" justifyContent="space-between">
          <ArrayFieldTitleTemplate
            idSchema={idSchema}
            title={uiOptions.title || title}
            schema={schema}
            uiSchema={uiSchema}
            required={required}
            registry={registry}
          />
          {canAdd && (
            <AddButton
              className="array-item-add"
              onClick={onAddClick}
              disabled={disabled || readonly}
              uiSchema={uiSchema}
              registry={registry}
            />
          )}
        </HStack>
      }
    >
      {items.length > 0 &&
        items.map(({ key, ...itemProps }: ArrayFieldTemplateItemType) => (
          <ArrayFieldItemTemplate key={key} {...itemProps} />
        ))}
    </ExpandingBox>
  );
};

const CustomAddButton: React.FC<IconButtonProps> = (props) => (
  <IconButton
    variant="ghost"
    onClick={props.onClick}
    icon={<AddIcon />}
    aria-label="Add"
  />
);

const CustomArrayFieldTitleTemplate: React.FC<ArrayFieldTitleProps> = (
  props,
) => <Text textStyle="bodyBold">{props.title}</Text>;

const templates: FormProps["templates"] = {
  FieldTemplate: CustomFieldTemplate,
  TitleFieldTemplate: CustomTitleFieldTemplate,
  ObjectFieldTemplate: CustomObjectFieldTemplate,
  ArrayFieldTemplate: CustomArrayFieldTemplate,
  ArrayFieldTitleTemplate: CustomArrayFieldTitleTemplate,
  ButtonTemplates: {
    AddButton: CustomAddButton,
    SubmitButton: CustomSubmitButton,
  },
};

/***
 * Flatten anyOf schemas that only indicate a nullable field.
 ***/
const fixupSchema = (schema: StrictRJSFSchema): StrictRJSFSchema => {
  const flattenProperties = (
    properties: Draft<{
      [p: string]: JSONSchema7Definition;
    }>,
  ) => {
    for (const [propertyKey, propertyValue] of Object.entries(properties)) {
      if (typeof propertyValue !== "object") {
        continue;
      }
      if (propertyValue.anyOf?.length === 2) {
        const nullIndex = propertyValue.anyOf.findIndex(
          (anyOf) => typeof anyOf === "object" && anyOf.type === "null",
        );
        if (nullIndex !== -1) {
          const otherElem = propertyValue.anyOf[0];
          if (typeof otherElem !== "object" || !otherElem.type) {
            continue;
          }
          otherElem.type = [otherElem.type, "null" as const].flat();
          properties[propertyKey] = otherElem;
        }
      }
    }
  };
  return produce(schema, (draft) => {
    if (draft.properties) {
      flattenProperties(draft.properties);
    }
    if (draft.$defs) {
      for (const value of Object.values(draft.$defs)) {
        if (typeof value !== "object") {
          continue;
        }
        const properties = value.properties;
        if (!properties) {
          continue;
        }
        flattenProperties(properties);
      }
    }
  });
};

const ExpandingBox: React.FC<
  React.PropsWithChildren<{
    title: React.ReactNode;
  }>
> = ({ title, children }) => (
  <Flex>
    <Accordion
      allowMultiple={true}
      borderWidth="1px"
      borderRadius="4px"
      borderColor="gray.300"
      boxShadow="sm"
      bg="white"
    >
      <AccordionItem
        borderTopWidth="0"
        sx={{
          _last: {
            borderBottomWidth: 0,
          },
          minWidth: "450px",
        }}
      >
        <AccordionButton as={Box} minHeight="48px">
          <AccordionIcon />
          {title}
        </AccordionButton>
        <AccordionPanel pl="36px">{children}</AccordionPanel>
      </AccordionItem>
    </Accordion>
  </Flex>
);
